import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import StepTitle from 'ui_patient/pages/MaskSelectorTool/StepTitle';
import MaskSelectorToolStyles from 'ui_patient/pages/MaskSelectorTool/MaskSelectorToolStyles';
import SimplePdfViewer from './SimplePdfViewer'; // Import the new component

import { useTranslation } from 'react-i18next';

const buttonNavigationHeight = '110px';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'block',
    '@media only screen and (max-width: 639px)': {
      height: `calc(100% - ${buttonNavigationHeight})`,
    },
    paddingTop: '14px',
  },
  pdfContainer: {
    width: '100%',
    height: 'calc(100vh - 300px)',
    '@media only screen and (max-width: 639px)': {
      height: 'calc(100vh - 350px)',
    },
    minHeight: '500px',
    '@media only screen and (max-width: 639px)': {
      minHeight: '300px',
    },
    border: `1px solid ${theme.palette.settings.border}`,
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '24px',
    display: 'flex', // Add this
    flexDirection: 'column', // Add this
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '24px',
    '@media only screen and (max-width: 639px)': {
      flexDirection: 'column',
    },
  },
  desktopButtons: {
    '@media only screen and (max-width: 639px)': {
      display: 'none',
    },
  },
  descriptionText: {
    color: theme.palette.text.primary,
    fontSize: '16px',
    lineHeight: '24px',
    marginBottom: '16px',
  },
  dialogTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '18px',
  },
  dialogContent: {
    color: theme.palette.text.primary,
  },
  dialogActions: {
    padding: '16px 24px',
  },
  dialogPaper: {
    borderRadius: '8px',
    padding: '8px',
  },
}));

const HipaaConsent = ({ handleAccept, handleDecline }) => {
  const { classes } = MaskSelectorToolStyles();
  const { classes: localClasses } = useStyles();
  const { t } = useTranslation('pages.MaskSelectorTool.HipaaConsent', {
    fallbackNS: 'pages.MaskSelectorTool.PatientInformation',
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Localization Strings
  const hipaaConsentTitle = t('hipaaConsentTitle', 'HIPAA Authorization');
  const hipaaConsentDescription = t(
    'hipaaConsentDescription',
    'Please review the HIPAA Authorization form below.'
  );
  const acceptButton = t('acceptButton', 'I Accept');
  const declineButton = t('declineButton', 'I Decline');
  const confirmDeclineTitle = t('confirmDeclineTitle', 'Confirm Decline');
  const confirmDeclineMessage = t(
    'confirmDeclineMessage',
    'If you decline the HIPAA Authorization, you will be returned to the start of the process for standard care. Please note that participation in this research and development is voluntary, but your authorization is required to share your data and take part in this R&D. Are you sure you want to decline?'
  );
  const confirmDeclineButton = t('confirmDeclineButton', 'Yes, Decline');
  const cancelButton = t('cancelButton', 'Cancel');

  const openConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const confirmDecline = () => {
    closeConfirmDialog();
    handleDecline();
  };

  return (
    <>
      <Box
        sx={{
          marginTop: '24px',
          paddingTop: '24px',
          marginBottom: '24px',
          minHeight: 'calc(100svh - 150px)',
        }}
        bgcolor="background.paper"
        className={classes.appRootPadding}
      >
        <StepTitle number="2" title={hipaaConsentTitle} />
        <Box className={localClasses.root}>
          <Typography
            variant="body1"
            paragraph
            className={localClasses.descriptionText}
          >
            {hipaaConsentDescription}
          </Typography>

          <Paper elevation={0} className={localClasses.pdfContainer}>
            {/* Replace iframe with SimplePdfViewer */}
            <SimplePdfViewer
              pdfUrl={`${process.env.PUBLIC_URL}/hipaa_authorization.pdf`}
              height="100%"
            />
          </Paper>

          {/* Desktop buttons */}
          <Box
            className={`${localClasses.buttonContainer} ${localClasses.desktopButtons}`}
          >
            <Button
              disableRipple
              variant="outlined"
              onClick={openConfirmDialog}
              data-testid="decline-button-desktop"
              sx={{ flex: 1 }}
            >
              {declineButton}
            </Button>
            <Button
              disableRipple
              variant="contained"
              onClick={handleAccept}
              data-testid="accept-button-desktop"
              sx={{ flex: 1 }}
            >
              {acceptButton}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Mobile buttons */}
      <Box className={classes.patient_continue_sticky}>
        <Box
          className={classes.patient_continue_sticky_inner}
          sx={{ height: 120 }}
        >
          <Box className={localClasses.buttonContainer} sx={{ marginTop: 0 }}>
            <Button
              disableRipple
              variant="outlined"
              onClick={openConfirmDialog}
              data-testid="decline-button-mobile"
              sx={{
                height: '30px',
                flex: 1,
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {declineButton}
            </Button>
            <Button
              disableRipple
              variant="contained"
              onClick={handleAccept}
              data-testid="accept-button-mobile"
              sx={{
                height: '30px',
                flex: 1,
                mb: 2,
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {acceptButton}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-decline-dialog-title"
        PaperProps={{
          className: localClasses.dialogPaper,
        }}
      >
        <DialogTitle
          id="confirm-decline-dialog-title"
          className={localClasses.dialogTitle}
        >
          {confirmDeclineTitle}
        </DialogTitle>
        <DialogContent>
          <Typography className={localClasses.dialogContent}>
            {confirmDeclineMessage}
          </Typography>
        </DialogContent>
        <DialogActions className={localClasses.dialogActions}>
          <Button onClick={closeConfirmDialog} variant="text" disableRipple>
            {cancelButton}
          </Button>
          <Button
            onClick={confirmDecline}
            variant="contained"
            disableRipple
            autoFocus
          >
            {confirmDeclineButton}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

HipaaConsent.propTypes = {
  handleAccept: PropTypes.func.isRequired,
  handleDecline: PropTypes.func.isRequired,
};

export default HipaaConsent;
